import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useSearchParams } from 'react-router-dom'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import ContractsTab from 'features/contracts/ContractsTab'
import ContractorsPage from 'features/contractors/ContractorsPage'

const TABS = [
  { key: 'contracts', label: 'Contract Management', icon: DescriptionOutlinedIcon },
  { key: 'contractors', label: 'Contractor Management', icon: HandshakeOutlinedIcon },
]
const VALID = new Set(TABS.map(t => t.key))

const ContractManagementTool = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const deepLinkContractorId = searchParams.get('contractor')
  const activeTab = useMemo(() => {
    if (deepLinkContractorId) return 'contractors'
    return requested && VALID.has(requested) ? requested : 'contracts'
  }, [requested, deepLinkContractorId])

  const setTab = key => setSearchParams(key === 'contracts' ? {} : { tab: key })

  return (
    <Wrapper>
      <PageHead>
        <PageTitle>Contract Management</PageTitle>
        <PageSubtitle>Track service contracts and your supply chain in one place.</PageSubtitle>
      </PageHead>

      <TabBar role='tablist' aria-label='Contract Management sections'>
        {TABS.map(t => {
          const Icon = t.icon
          const active = activeTab === t.key
          return (
            <Tab
              key={t.key}
              type='button'
              role='tab'
              aria-selected={active}
              $active={active}
              onClick={() => setTab(t.key)}
            >
              <Icon style={{ fontSize: 18 }} />
              <span>{t.label}</span>
            </Tab>
          )
        })}
      </TabBar>

      <TabPanel role='tabpanel'>
        {activeTab === 'contracts' && <ContractsTab />}
        {activeTab === 'contractors' && <ContractorsPage />}
      </TabPanel>
    </Wrapper>
  )
}

export default ContractManagementTool

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PageHead = styled.div`
  padding: 4px 4px 0;
  @media (max-width: 768px) { padding: 0 4px; }
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #0f172a;
  @media (max-width: 768px) { font-size: 18px; }
`

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;

  @media (max-width: 768px) { font-size: 12.5px; }
`

const TabBar = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 999px;
  align-self: flex-start;
  flex-wrap: wrap;
  max-width: 100%;
  box-sizing: border-box;

  /* Full width on mobile so the two tabs read as a proper segmented control
     instead of a pill that trails off the edge. */
  @media (max-width: 768px) {
    align-self: stretch;
    width: 100%;
    border-radius: 14px;
  }
`

const Tab = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: all 160ms ease;
  background: ${({ $active }) => ($active ? '#0f172a' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#475569')};

  &:hover {
    background: ${({ $active }) => ($active ? '#0f172a' : '#e5e7eb')};
  }

  @media (max-width: 768px) {
    flex: 1 1 auto;
    justify-content: center;
    min-width: 0;
    padding: 9px 12px;
    border-radius: 11px;
    font-size: 12.5px;
    span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
`

const TabPanel = styled.div`
  min-height: 0;
`
